import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Broadcast, Gear, SignOut, SquaresFour, ChatCircleDots } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setMode(data)).catch(() => {});
  }, [location.pathname]);

  const doLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const navItem = (to, label, Icon) => {
    const active = location.pathname === to;
    return (
      <Link
        to={to}
        data-testid={`nav-${label.toLowerCase()}`}
        className={`flex items-center gap-2 px-3 h-9 text-sm font-medium border transition-colors duration-150 ${
          active
            ? "bg-sv-ink text-sv-onink border-sv-ink"
            : "bg-transparent text-sv-secondary border-sv-border hover:bg-sv-hover hover:text-sv-ink"
        }`}
      >
        <Icon size={16} weight="bold" />
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-sv-bg text-sv-ink">
      <header className="sticky top-0 z-30 border-b border-sv-border bg-sv-bg/95 backdrop-blur">
        <div className="max-w-[1400px] mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="brand-home-link">
            <Broadcast size={22} weight="bold" className="text-sv-live" />
            <span className="font-display font-black text-lg tracking-tight uppercase">Sendaba</span>
          </Link>
          <div className="flex items-center gap-2">
            {navItem("/dashboard", "Campaigns", SquaresFour)}
            {navItem("/conversations", "Conversations", ChatCircleDots)}
            {navItem("/manage", "Manage", Gear)}
            {mode && (
              <Link
                to="/manage"
                data-testid="send-mode-badge"
                title={mode.use_mock ? "Mock mode: messages are simulated" : "Live mode: real SMS are sent"}
                className="flex items-center gap-1.5 px-3 h-9 border font-mono text-[11px] uppercase tracking-wider transition-colors duration-150"
                style={
                  mode.use_mock
                    ? { color: "#B45309", borderColor: "#B4530955", background: "#B4530912" }
                    : { color: "#16A34A", borderColor: "#16A34A55", background: "#16A34A12" }
                }
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: mode.use_mock ? "#B45309" : "#16A34A" }}
                />
                {mode.use_mock ? "MOCK" : "LIVE"}
              </Link>
            )}
            <div className="hidden sm:flex items-center gap-2 pl-3 ml-1 border-l border-sv-border">
              <span className="font-mono text-xs text-sv-muted max-w-[160px] truncate" data-testid="current-user-email">
                {user?.email}
              </span>
              <button
                data-testid="logout-button"
                onClick={doLogout}
                className="flex items-center gap-1.5 px-3 h-9 text-sm border border-sv-border text-sv-secondary hover:text-sv-stop hover:border-sv-stop transition-colors duration-150"
              >
                <SignOut size={16} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-8">{children}</main>
    </div>
  );
}
